import { useEffect, useRef, useState } from 'react';
import Button from '@/components/elements/Button';
import Container from '@/components/global/Container';
import { TitleAndDescription } from '@/components/shared/TitleAndDescription';
import { IComponentBlocksStarStoryForm, IComponentSharedTitleAndDescription } from '@/graphql/__generated__/global/types';
import { IComponentSharedTab } from '@/graphql/__generated__/global/types';
import Link from 'next/link';
import { StarStoryFormTabContent } from './StarStoryFormTabContent';

export enum IStarStoryFormTabsTitles {
    About = 'About',
    Story = 'Story',
    Upload = 'Upload',
}
export interface IStarStoryFormTabs extends IComponentSharedTab {
    title: IStarStoryFormTabsTitles;
    inputFields: Record<string, { label: string; placeholder?: string }>;
    tabDescription?: string;
}

const BlockStarStoryForm = ({ titleDescription, aboutDescription, storyDescription, uploadDescription, termsAndConditions }: IComponentBlocksStarStoryForm) => {
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const submitStarStoryRef = useRef<HTMLDivElement>(null);

    const scrollToTop = () => {
        if (submitStarStoryRef.current) {
            submitStarStoryRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    useEffect(() => {
        if (submissionSuccess) {
            scrollToTop();
        }
    }, [submissionSuccess]);

    const formTabs: IStarStoryFormTabs[] = [
        {
            id: 'about',
            title: IStarStoryFormTabsTitles.About,
            inputFields: {
                firstName: {
                    label: 'First Name',
                },
                lastName: { label: 'Last Name' },
                email: { label: 'Email' },
            },
            tabDescription: aboutDescription,
        },
        {
            id: 'story',
            title: IStarStoryFormTabsTitles.Story,
            inputFields: {
                roadTripReason: {
                    label: 'Why did you decide to go on a road trip?',
                    placeholder: 'Bucket list, time out, adventure, reconnection…',
                },
                travelPartners: {
                    label: 'Who did you travel with?',
                    placeholder: 'Friends, partner, kids…',
                },
                whereAndWhy: {
                    label: 'Where did you go and why did you choose this area?',
                    placeholder: 'Mountains, Desert, Ocean...',
                },
                favouriteExperience: {
                    label: 'What was your favourite experience along the way?',
                    placeholder: 'Getting lost, conquering a great walk, early morning sunrises…',
                },
                madeYouFeel: {
                    label: 'Thinking back on your trip, the people you travelled with, the places you visited, the people you met, the experiences you had, describe how it made you feel.',
                    placeholder: 'Free, connected, thrilled…',
                },
            },
            tabDescription: storyDescription,
        },
        {
            id: 'upload',
            title: IStarStoryFormTabsTitles.Upload,
            inputFields: {
                images: { label: 'Image' },
                videos: { label: 'Video' },
                terms: { label: 'I accept the terms' },
                updatesAndDeals: {
                    label: 'I want to receive updates and deals from Star RV',
                },
            },
            tabDescription: uploadDescription,
        },
    ];

    return (
        <Container bg="shell" rounded wrapperClassName="py-16">
            {submissionSuccess ? (
                <div className="text-center" ref={submitStarStoryRef}>
                    <TitleAndDescription title="Thank you for Submitting!" description="We will review your submission and get back to you shortly." id={''} />
                    <Link href="/" passHref title="Go to homepage" className={'transition duration-500 ease-out'} prefetch={false}>
                        <Button className="button-lg">Back to home</Button>
                    </Link>
                </div>
            ) : (
                <>
                    <a id="sumbit-story-form"></a>
                    {titleDescription && <TitleAndDescription {...(titleDescription as IComponentSharedTitleAndDescription)} />}
                    <StarStoryFormTabContent tabItems={formTabs} termsAndConditions={termsAndConditions} submissionSuccess={submissionSuccess} setSubmissionSuccess={setSubmissionSuccess} />
                </>
            )}
        </Container>
    );
};

export default BlockStarStoryForm;
